// COPIADO de src/domain/schedule.ts — não edite aqui; edite no gestor
/**
 * schedule.ts — regras puras de "QUANDO EXIBIR".
 *
 * Decisões de arquitetura (valem para o backend e para o receptor):
 *
 * 1. O domínio NUNCA lê o relógio. `now: Date` é sempre parâmetro. Isso deixa
 *    os testes determinísticos e permite ao receptor avaliar a programação
 *    offline (sem servidor) usando o relógio do dispositivo.
 *
 * 2. Limites são INCLUSIVOS nas duas pontas: `startsAt <= now <= endsAt`.
 *    Um anúncio que termina exatamente 18:00 ainda aparece 18:00:00.000.
 *    Escolha deliberada: o comerciante que digita 18:00 espera que o anúncio
 *    apareça às 18:00, não que desapareça no mesmo segundo.
 *
 * 3. Fuso horário: `AdSchedule` carrega ISO em UTC, mas "Toda semana 08:00-18:00"
 *    é interpretado no FUSO DE EXIBIÇÃO (o fuso da praça onde a TV está, não o
 *    do servidor). A conversão é feita por `opts.timeZoneOffsetMinutes`
 *    (minutos a SOMAR ao UTC, ex.: Brasil UTC-3 => -180). Sem o parâmetro,
 *    usamos o fuso local do dispositivo que está avaliando — no app gestor isso
 *    é o fuso do lojista, que é o comportamento esperado na v1.
 *
 *    CONTRATO DEFINITIVO (decisão D9 — não mudar sem `timezone` por tela):
 *    enquanto NÃO existir um campo `timezone` por tela no backend, o dono da
 *    avaliação de `weekly` é o RECEPTOR, com o relógio da própria TV. O backend
 *    **não deve avaliar `weekly`**: ele envia a faixa crua (`weekly.days`,
 *    `weekly.startTime`, `weekly.endTime`) no payload da fila e deixa a TV
 *    decidir. Motivo: o servidor roda em outro fuso; avaliar lá significaria
 *    dizer "está na faixa" com o relógio errado e divergir de forma silenciosa
 *    (a conta mostra "Ativo" e a TV não exibe). O que o backend PODE filtrar sem
 *    fuso é `period`/`now` — esses são instantes absolutos (ISO em UTC).
 *    Multi-praça com fusos diferentes exige `timezone` por tela (coluna nova no
 *    backend); só então `timeZoneOffsetMinutes` deixa de ser opcional na borda
 *    e o backend ganha o direito de avaliar `weekly`.
 *    Ver docs/architecture/ARCHITECTURE.md §5.
 * 4. `weekly` com `endTime < startTime` significa faixa que CRUZA a meia-noite
 *    (22:00-02:00 => abre naquele dia e fecha no dia seguinte). `startTime ===
 *    endTime` é interpretado como **24 h** ("o dia inteiro") nos dias marcados —
 *    evita uma faixa de 0 minuto que nunca exibiria nada (erro silencioso
 *    clássico de scheduler) e é o que dá sentido a `00:00-00:00`. `'00:00'` é
 *    um horário VÁLIDO (= 0 minuto): `00:00-23:59` é aceito no domínio
 *    (`weeklySchedule`), nunca rejeitado por "falsy".
 */

import type { Ad, AdStatus, ID, ISO, WeeklyWindow } from './types';

/** Opções de avaliação temporal (tudo opcional — assinatura mínima é (ad, now)). */
export interface ScheduleEvalOptions {
  /**
   * Minutos a somar ao horário UTC para obter o horário de exibição.
   * Ex.: Brasil (UTC-3) => -180. Omitir => usa o fuso local do dispositivo.
   *
   * Quem chama decide o fuso; o domínio nunca lê o relógio. Sem uma `timezone`
   * por tela (v2), só o RECEPTOR deve avaliar `weekly` — o backend envia a faixa
   * crua. Ver o cabeçalho deste arquivo (item 3).
   */
  timeZoneOffsetMinutes?: number;
}

/* Fuso: a aritmética aqui é feita em SEGUNDOS desde a meia-noite (ver `localParts`). */

/** Segundos desde a meia-noite de um relógio no formato 'HH:mm' ou 'HH:mm:ss'. */
export function parseClockToSeconds(value: string | undefined): number | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = match[3] ? Number(match[3]) : 0;
  if (hours > 23 || minutes > 59 || seconds > 59) {
    return null;
  }
  return hours * 3600 + minutes * 60 + seconds;
}

/** Converte ISO em Date, retornando null para entradas inválidas (dado de API suja). */
function parseISO(value: ISO | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

/** Parte "dia da semana + segundos do dia" do instante, no fuso de exibição. */
function localParts(
  now: Date,
  opts?: ScheduleEvalOptions,
): { day: number; secondsOfDay: number } {
  const offset = opts?.timeZoneOffsetMinutes;
  if (typeof offset === 'number' && Number.isFinite(offset)) {
    const shifted = new Date(now.getTime() + offset * 60 * 1000);
    return {
      day: shifted.getUTCDay(),
      secondsOfDay:
        shifted.getUTCHours() * 3600 +
        shifted.getUTCMinutes() * 60 +
        shifted.getUTCSeconds(),
    };
  }
  return {
    day: now.getDay(),
    secondsOfDay: now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds(),
  };
}

/**
 * O instante cai dentro da faixa semanal? Testável isoladamente (útil para o
 * receptor validar a própria programação offline).
 */
export function weeklyWindowContains(
  window: WeeklyWindow | undefined,
  now: Date,
  opts?: ScheduleEvalOptions,
): boolean {
  if (!window || !Array.isArray(window.days) || window.days.length === 0) {
    return false;
  }
  const start = parseClockToSeconds(window.startTime);
  const end = parseClockToSeconds(window.endTime);
  if (start === null || end === null) {
    // Faixa malformada nunca "vaza" para exibição: melhor não exibir do que exibir errado.
    return false;
  }

  const { day, secondsOfDay } = localParts(now, opts);
  const startDay = ((day % 7) + 7) % 7;
  const previousDay = (startDay + 6) % 7;
  const includesDay = (d: number): boolean => window.days.indexOf(d) !== -1;

  // Dia inteiro: faixa degenerada vira 24h no(s) dia(s) marcado(s).
  if (start === end) {
    return includesDay(startDay);
  }

  // Faixa normal (mesmo dia): 08:00-18:00.
  if (start < end) {
    return includesDay(startDay) && secondsOfDay >= start && secondsOfDay <= end;
  }

  // Faixa que cruza a meia-noite: 22:00-02:00.
  // Parte 1: hoje é dia marcado e já passou do início (22:00-23:59).
  // Parte 2: ONTEM era dia marcado e ainda estamos antes do fim (00:00-02:00).
  if (includesDay(startDay) && secondsOfDay >= start) {
    return true;
  }
  if (includesDay(previousDay) && secondsOfDay <= end) {
    return true;
  }
  return false;
}

/**
 * `true` quando o anúncio deve estar RODANDO agora.
 * Combina status publicado + janela de agendamento. Não considera `screenIds`
 * (isso é papel de `selectActiveAdsForScreen`/`buildPlaybackQueue`).
 */
export function isAdActiveAt(ad: Ad, now: Date, opts?: ScheduleEvalOptions): boolean {
  if (!ad || ad.status !== 'active') {
    return false;
  }
  const schedule = ad.schedule;
  if (!schedule) {
    return false;
  }

  // Envelope opcional de validade, aplicado a QUALQUER modo (um anúncio
  // "toda semana, seg-sex" pode ter prazo final "até 30/09").
  const endsAt = parseISO(schedule.endsAt);
  if (endsAt && now.getTime() > endsAt.getTime()) {
    return false;
  }
  const startsAt = parseISO(schedule.startsAt);

  switch (schedule.mode) {
    case 'now':
      // "Exibir agora": já publicado, sem janela de início. Sai quando o
      // usuário pausar/excluir ou quando `endsAt` passar (se houver).
      return true;

    case 'period': {
      if (startsAt && now.getTime() < startsAt.getTime()) {
        return false;
      }
      if (!startsAt && !endsAt) {
        // Período sem nenhuma ponta é dado inconsistente; tratamos como ativo
        // (o usuário já publicou e o backend deveria ter preenchido as datas).
        return true;
      }
      return true;
    }

    case 'weekly': {
      if (startsAt && now.getTime() < startsAt.getTime()) {
        return false;
      }
      return weeklyWindowContains(schedule.weekly, now, opts);
    }

    default:
      // Modo desconhecido vindo de uma API mais nova: não exibir.
      return false;
  }
}

/**
 * Status "de verdade" agora, derivado de `status` + relógio.
 * `expired` é o único estado que o backend/UI precisam propagar sozinhos:
 * o anúncio continua `active` no banco até alguém marcar como vencido (ou o
 * backend faz isso no job de varredura), mas a UI precisa mostrar "Encerrado".
 *
 * Nota: um anúncio `active` com `startsAt` no futuro continua retornando
 * 'active' (está publicado, só não começou) — `isAdActiveAt` é quem diz se está
 * no ar. Ver PLAYER.md para como o receptor trata o caso.
 */
export function adRuntimeStatus(ad: Ad, now: Date): AdStatus {
  if (ad.status === 'expired' || ad.status === 'draft' || ad.status === 'paused') {
    return ad.status;
  }
  const endsAt = parseISO(ad.schedule?.endsAt);
  if (endsAt && now.getTime() > endsAt.getTime()) {
    return 'expired';
  }
  return 'active';
}

/** Código curto de pareamento: 6 dígitos, sem 0 inicial ambíguo para digitação. */
export const PAIRING_CODE_LENGTH = 6;

const PAIRING_ALPHABET = '123456789';

/**
 * Gera código de pareamento determinístico a partir de uma semente
 * (o backend passa a própria entropia; o mock passa um índice/versão).
 * Sem `Math.random` aqui: o domínio é puro e testável.
 */
export function pairingCodeFromSeed(seed: number): string {
  let value = Math.floor(Math.abs(seed)) % Math.pow(10, PAIRING_CODE_LENGTH);
  let code = '';
  for (let i = 0; i < PAIRING_CODE_LENGTH; i += 1) {
    const digit = value % 10;
    code = PAIRING_ALPHABET[digit === 0 ? 0 : digit] + code;
    value = Math.floor(value / 10);
  }
  return code;
}

/** Helper: id legível para logs ("tela:TV Balcão"). */
export function describeScreenTarget(screenIds: ID[]): string {
  return screenIds.length === 0 ? 'nenhuma tela' : `${screenIds.length} tela(s)`;
}
